import React, { useState, useEffect } from 'react';
import { getConsent } from '../../utils/cookieConsent';
import './CookiePreferences.css';

const CookiePreferences = ({ isOpen, onClose, onSave }) => {
  const [preferences, setPreferences] = useState({
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Load existing preferences
    const consent = getConsent();
    if (consent) {
      setPreferences({
        functional: consent.preferences.functional || false,
        analytics: consent.preferences.analytics || false,
        marketing: consent.preferences.marketing || false,
      });
    }
  }, [isOpen]);

  const handleToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    onSave(allAccepted);
  };

  if (!isOpen) return null;

  return (
    <div className="cookie-modal-overlay" onClick={onClose}>
      <div className="cookie-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cookie-modal-header">
          <h2>Cookie Preferences</h2>
          <button className="cookie-modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="cookie-modal-body">
          <p className="cookie-modal-intro">
            We use cookies to enhance your experience. You can choose which types of cookies to allow. Please note that blocking some types of cookies may impact your experience on our site.
          </p>

          {/* Strictly Necessary */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-title">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    aria-label="Strictly necessary cookies - always enabled"
                  />
                  <span className="cookie-toggle-slider disabled"></span>
                </label>
                <div>
                  <h3>Strictly Necessary Cookies</h3>
                  <span className="cookie-badge">Always Active</span>
                </div>
              </div>
            </div>
            <p className="cookie-category-description">
              These cookies are essential for the website to function properly. They enable basic functions like page navigation, access to secure areas, and cannot be disabled.
            </p>
            <details className="cookie-details">
              <summary>View Details</summary>
              <ul>
                <li>Session management</li>
                <li>Security tokens (CSRF protection)</li>
                <li>Load balancing</li>
                <li>Cookie consent preferences</li>
              </ul>
            </details>
          </div>

          {/* Functional */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-title">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={() => handleToggle('functional')}
                    aria-label="Functional cookies"
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
                <div>
                  <h3>Functional Cookies</h3>
                </div>
              </div>
            </div>
            <p className="cookie-category-description">
              These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
            </p>
            <details className="cookie-details">
              <summary>View Details</summary>
              <ul>
                <li>Language preferences</li>
                <li>Region preferences</li>
                <li>UI customization settings</li>
                <li>Remembered form inputs</li>
              </ul>
            </details>
          </div>

          {/* Analytics */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-title">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handleToggle('analytics')}
                    aria-label="Analytics cookies"
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
                <div>
                  <h3>Analytics & Performance Cookies</h3>
                </div>
              </div>
            </div>
            <p className="cookie-category-description">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
            </p>
            <details className="cookie-details">
              <summary>View Details</summary>
              <ul>
                <li><strong>Google Analytics:</strong> Website traffic analysis (anonymous)</li>
                <li>Page views and user behavior patterns</li>
                <li>Performance metrics</li>
                <li>Error tracking</li>
              </ul>
              <p className="cookie-retention"><strong>Retention:</strong> Up to 2 years</p>
            </details>
          </div>

          {/* Marketing */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-title">
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handleToggle('marketing')}
                    aria-label="Marketing cookies"
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
                <div>
                  <h3>Marketing & Advertising Cookies</h3>
                </div>
              </div>
            </div>
            <p className="cookie-category-description">
              These cookies are used to deliver personalized advertisements and track campaign effectiveness. They may also be used to build a profile of your interests.
            </p>
            <details className="cookie-details">
              <summary>View Details</summary>
              <ul>
                <li>Advertising campaign tracking</li>
                <li>Personalized ad delivery</li>
                <li>Remarketing</li>
                <li>Social media integration</li>
              </ul>
              <p className="cookie-retention"><strong>Retention:</strong> Up to 1 year</p>
            </details>
          </div>
        </div>

        <div className="cookie-modal-footer">
          <button className="btn btn-secondary" onClick={handleSave}>
            Save Preferences
          </button>
          <button className="btn btn-primary" onClick={handleAcceptAll}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;
